import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-slate-400/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border shadow-xs",
        secondary:
          "shadow-xs",
        ghost: "",
        link: "text-primary underline-offset-4 hover:underline",
        "most-popular": 
          "bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0 shadow-[0_4px_20px_rgba(249,115,22,0.4)] font-bold uppercase tracking-wider text-base transition-all duration-200 hover:from-orange-600 hover:to-orange-700 hover:shadow-[0_8px_30px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_4px_20px_rgba(249,115,22,0.4)]",
        "regular": 
          "bg-[#53bdeb] text-white shadow-md font-semibold uppercase tracking-wide transition-all duration-200 hover:bg-[#259ae9] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm",
        // The AI surfaces' primary action (instinct's CTA pill): ink on light,
        // white on dark, via the --ai-* tokens. Plain hover:/active: (not enabled:)
        // so it also works asChild as a link; disabled already drops pointer events.
        // Pill radius is in compoundVariants
        // so it wins over the sm/lg sizes' rounded-md.
        ai:
          "border-0 bg-ai-text text-ai-bg shadow-sm transition-[transform,opacity] duration-(--ai-duration-fast) ease-ai hover:scale-[1.03] hover:opacity-95 active:scale-[0.98] focus-visible:ring-ai-focus-ring",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-md px-8 has-[>svg]:px-5 text-base",
        icon: "size-9",
      },
    },
    compoundVariants: [{ variant: "ai", className: "rounded-full" }],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Apply theme-aware styles based on variant
    let themeStyle: React.CSSProperties = {}
    if (variant === 'outline') {
      themeStyle = {
        backgroundColor: 'var(--color-surface, #ffffff)',
        color: 'var(--color-text, #111827)',
        borderColor: 'var(--color-border, #e5e7eb)',
      }
    } else if (variant === 'secondary') {
      themeStyle = {
        backgroundColor: 'var(--color-surface-muted, #f3f4f6)',
        color: 'var(--color-text, #111827)',
      }
    } else if (variant === 'ghost') {
      themeStyle = {
        color: 'var(--color-text, #111827)',
      }
    } else if (variant === 'destructive') {
      themeStyle = {
        backgroundColor: 'var(--color-destructive, #ef4444)',
        color: 'var(--color-destructive-foreground, #ffffff)',
      }
    }
    
    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        style={{ ...themeStyle, ...style }}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
